import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Truck, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, logout, isAuthenticated, isMechanic, isAtelier, user } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(false);

  const getRoleRedirect = (role) => {
    if (role === 'mechanic') return '/admin/mecanicien';
    if (role === 'atelier') return '/admin/fleet';
    return '/admin/dashboard';
  };

  const handleSwitchAccount = () => {
    logout();
    setShowLoginForm(true);
  };

  const handleGoToDashboard = () => {
    navigate(getRoleRedirect(user?.role));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast.success('Connexion réussie');
      navigate(getRoleRedirect(result.user?.role));
    } else {
      setError(result.error);
      toast.error(result.error);
    }

    setLoading(false);
  };

  // Show switch account screen if already logged in
  if (isAuthenticated && !showLoginForm) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center mx-auto mb-4 bg-slate-900 ring-1 ring-amber-500/30">
              <img src="/icons/icon-192.png" alt="ProPilote" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-2xl font-bold text-white">ProPilote Admin</h1>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-center">
            <p className="text-slate-400 text-sm mb-2">Connecte en tant que</p>
            <p className="text-white font-semibold text-lg">{user?.first_name} {user?.last_name}</p>
            <p className="text-amber-500 text-sm mb-6">{user?.role === 'mechanic' ? 'Mecanicien' : user?.role === 'atelier' ? 'Atelier' : 'Administrateur'}</p>
            <div className="space-y-3">
              <button
                onClick={handleGoToDashboard}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 rounded-lg transition-colors"
                data-testid="go-to-dashboard-btn"
              >
                Aller au tableau de bord
              </button>
              <button
                onClick={handleSwitchAccount}
                className="w-full border border-slate-700 text-slate-300 hover:bg-slate-800 font-medium py-3 rounded-lg transition-colors"
                data-testid="switch-account-btn"
              >
                Changer de compte
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center mx-auto mb-4 bg-slate-900 ring-1 ring-amber-500/30">
            <img src="/icons/icon-192.png" alt="ProPilote" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-2xl font-bold text-white">ProPilote Admin</h1>
          <p className="text-slate-400 mt-1">Portail de gestion de flotte</p>
        </div>

        {/* Login Form */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="text-red-500 flex-shrink-0" size={18} />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm text-slate-400 mb-2">Courriel</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg py-3 pl-10 pr-4 text-white focus:outline-none focus:border-amber-500"
                  placeholder="admin@entreprise.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg py-3 pl-10 pr-12 text-white focus:outline-none focus:border-amber-500"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-800">
            <p className="text-center text-slate-400 text-sm">
              Pas encore de compte ?{' '}
              <button
                onClick={() => navigate('/admin/register')}
                className="text-amber-500 hover:text-amber-400 font-medium"
                data-testid="register-link"
              >
                Essai gratuit 30 jours
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
